import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { notEmpty } from "shared/lib/utils";

import type { ContentHeadlineVariant } from "scmp-app/components/content/content-headline";
import { MagazinesStyleTagsMap } from "scmp-app/components/content/content-headline/content-headline-tag/variants/magazines-style/const";
import type { contentHeadlineTagContent$key } from "scmp-app/queries/__generated__/contentHeadlineTagContent.graphql";

import { useDetermineHeadlineTag } from "./hooks";
import { Description, Flag, FlagDivider, Heading, StyledTooltip } from "./styles";

export type Props = {
  className?: string;
  hideTooltip?: boolean;
  reference: contentHeadlineTagContent$key;
  useSectionTags?: boolean;
  variant?: ContentHeadlineVariant;
};

export const ContentHeadlineTag: FunctionComponent<Props> = ({
  className,
  hideTooltip,
  reference,
  useSectionTags = false,
  variant,
}) => {
  const content = useFragment(
    graphql`
      fragment contentHeadlineTagContent on Content {
        ...hooksContentHeadlineTagContent
      }
    `,
    reference,
  );
  const { color, description, entityId, name } =
    useDetermineHeadlineTag(content, useSectionTags) ?? {};

  if (!name) return null;

  const renderFlag = () => {
    const getFlagContent = () => {
      switch (variant) {
        case "generic":
          return <>{name}</>;
        case "magazines-style":
          return (
            <>
              {entityId ? MagazinesStyleTagsMap[entityId] : name}
              <FlagDivider> / </FlagDivider>
            </>
          );
        default:
          return (
            <>
              {name}
              <FlagDivider> | </FlagDivider>
            </>
          );
      }
    };

    return (
      <Flag $color={color} $variant={variant} className={className}>
        {getFlagContent()}
      </Flag>
    );
  };

  const renderName = () => {
    if (!notEmpty(name)) return null;
    return <Heading>{name}</Heading>;
  };

  const renderDescription = () => {
    if (!notEmpty(description)) return null;
    return <Description>{description}</Description>;
  };

  if (hideTooltip === true || !notEmpty(description)) return renderFlag();

  return (
    <StyledTooltip
      arrow
      placement="bottom-start"
      title={
        <>
          {renderName()}
          {renderDescription()}
        </>
      }
    >
      {renderFlag()}
    </StyledTooltip>
  );
};

ContentHeadlineTag.displayName = "ContentHeadlineTag";
